import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';

export interface AdminUsuario {
  id: string;
  nome: string;
  email: string;
  vendedor_erp_id: number | null;
  perfil: 'admin' | 'gerente' | 'vendedor' | 'representante';
  cliente_id: number;
  ativo: boolean;
  created_at: string;
}

export interface CreateUsuarioPayload {
  nome: string;
  email: string;
  password: string;
  vendedor_erp_id: number | null;
  perfil: 'admin' | 'gerente' | 'vendedor' | 'representante';
}

export function useAdminUsuarios() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['admin-usuarios', user?.client_id],
    enabled: user?.perfil === 'admin',
    queryFn: async (): Promise<AdminUsuario[]> => {
      const res = await api.get<{ usuarios: AdminUsuario[] }>('/admin/usuarios');
      return res.data.usuarios;
    }
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CreateUsuarioPayload) => {
      const res = await api.post('/admin/usuarios', payload);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-usuarios'] })
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: Partial<AdminUsuario> & { id: string }) => {
      await api.put(`/admin/usuarios/${id}`, payload);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-usuarios'] })
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, ativo }: { id: string; ativo: boolean }) => {
      await api.patch(`/admin/usuarios/${id}/status`, { ativo });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-usuarios'] }),
    onError: (err: any) => {
      // AIDEV-NOTE: centralizar em snackbar global quando disponível
      console.error('Erro ao alterar status:', err?.response?.data?.error ?? String(err));
    }
  });

  const changePasswordMutation = useMutation({
    mutationFn: async ({ id, nova_senha }: { id: string; nova_senha: string }) => {
      await api.patch(`/admin/usuarios/${id}/senha`, { nova_senha });
    }
  });

  const syncPedidosErpMutation = useMutation({
    mutationFn: async (vendedor_erp_id: number) => {
      const res = await api.post<{ jobId: string }>(
        '/sync/importar-pedidos-erp',
        { vendedor_erp_id },
        // AIDEV-NOTE: timeout maior que o global (30s) — rota tem 600s no nginx (import pode demorar)
        { timeout: 600_000 }
      );
      return res.data; // { jobId }
    }
  });

  return {
    ...query,
    createMutation,
    updateMutation,
    toggleStatusMutation,
    changePasswordMutation,
    syncPedidosErpMutation
  };
}

export interface ImportJobStatus {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: { processed: number; total: number };
  result?: { imported: number; skipped: number; errors: number; errorDetails: string[] };
  error?: string;
}

export function useImportJob(jobId: string | null) {
  return useQuery({
    queryKey: ['import-job', jobId],
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'running' ? 2000 : false;
    },
    queryFn: async (): Promise<ImportJobStatus> => {
      const res = await api.get<ImportJobStatus>(`/sync/jobs/${jobId}`);
      return res.data;
    }
  });
}

export function useAdminLookups() {
  const vendedores = useQuery({
    queryKey: ['admin-lookup-vendedores'],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ vendedores: { codigo: number; nome: string; email: string }[] }>(
        '/admin/lookup/vendedores-firebird'
      );
      return res.data.vendedores;
    }
  });

  const tabelas = useQuery({
    queryKey: ['admin-lookup-tabelas'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ tabelas: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tabelas-preco'
      );
      return res.data.tabelas;
    }
  });

  const condicoes = useQuery({
    queryKey: ['admin-lookup-condicoes'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ condicoes: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/condicoes-pagto'
      );
      return res.data.condicoes;
    }
  });

  const origens = useQuery({
    queryKey: ['admin-lookup-origens'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ origens: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/origens-pedido'
      );
      return res.data.origens;
    }
  });

  const pontosEstoque = useQuery({
    queryKey: ['admin-lookup-pontos-estoque'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ pontos: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/pontos-estoque'
      );
      return res.data.pontos;
    }
  });

  const empresas = useQuery({
    queryKey: ['admin-lookup-empresas'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ empresas: { codigo: number; nome_fantasia: string }[] }>(
        '/admin/lookup/empresas'
      );
      return res.data.empresas;
    }
  });

  const grupos = useQuery({
    queryKey: ['admin-lookup-grupos'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ grupos: string[]; subgrupos: string[] }>(
        '/admin/lookup/grupos-produtos'
      );
      return res.data;
    }
  });

  const tiposComercializacao = useQuery({
    queryKey: ['admin-lookup-tipos-comercializacao'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ tipos: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tipos-comercializacao'
      );
      return res.data.tipos;
    }
  });

  const tiposMovimentoReceber = useQuery({
    queryKey: ['admin-lookup-tipos-movimento-receber'],
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const res = await api.get<{ tipos: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tipos-movimento-receber'
      );
      return res.data.tipos;
    }
  });

  return {
    vendedores,
    tabelas,
    condicoes,
    origens,
    pontosEstoque,
    empresas,
    grupos,
    tiposComercializacao,
    tiposMovimentoReceber
  };
}
